import React, { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Stack, TextField } from "@mui/material";

import { useApp } from "../../../hooks/useApp";

type AddNetworkModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<"add-network" | "remove-network" | null>>;
};

type AddNetworkFormData = {
  name: string;
  address: string;
  description?: string;
};

const initialFormData: AddNetworkFormData = {
  name: "",
  address: "",
  description: "",
};

const initialErrorData = initialFormData;

export const AddNetworkModal: React.FC<AddNetworkModalProps> = ({ open, setOpen }) => {
  const app = useApp();

  const [formData, setFormData] = useState<AddNetworkFormData>(initialFormData);
  const [formError, setFormError] = useState<AddNetworkFormData>(initialErrorData);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleClose = () => {
    setFormData(initialFormData);
    setFormError(initialErrorData);
    setOpen(null);
  };

  const handleAdd = () => {
    setFormError(initialErrorData);

    const isValid = validateForm();

    if (!isValid) return;

    app.addNetwork(formData.name, formData.address, formData.description || "");
    handleClose();

    setOpenSnackbar(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newFormData: any = { ...formData };

    newFormData[e.target.name] = e.target.value;

    setFormData(newFormData as AddNetworkFormData);
  };

  const validateForm = () => {
    let isValid = true;

    const newErrorData = { ...initialErrorData };

    if (!formData.name) {
      isValid = false;
      newErrorData.name = "This field is required";
    }

    if (!formData.address) {
      isValid = false;
      newErrorData.address = "This field is required";
    }

    setFormError((prev) => ({ ...prev, ...newErrorData }));

    return isValid;
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackbar(false);
  };

  return (
    <React.Fragment>
      <Dialog //
        open={open}
        onClose={handleClose}
        scroll={"paper"}
        fullWidth={true}
        maxWidth={"xs"}
        aria-labelledby="add-network-title"
        aria-describedby="add-network-description"
      >
        <DialogTitle id="add-network-title">Add network</DialogTitle>
        <DialogContent dividers={true}>
          <DialogContentText id="add-network-description" tabIndex={-1}>
            <Stack direction={"column"} gap={"1rem"} sx={{ width: "100%", py: "1rem" }}>
              <TextField
                id="name"
                name="name"
                label="Name"
                variant="filled"
                value={formData.name}
                onChange={(e) => {
                  handleChange(e);
                }}
                autoFocus
                size={"small"}
                error={!!formError.name}
                helperText={formError.name}
              />
              <TextField
                id="address"
                name="address"
                label="Address"
                variant="filled"
                value={formData.address}
                onChange={(e) => {
                  handleChange(e);
                }}
                size={"small"}
                error={!!formError.address}
                helperText={formError.address}
              />
              <TextField
                id="description"
                name="description"
                label="Description"
                variant="filled"
                multiline
                minRows={1}
                maxRows={5}
                value={formData.description}
                onChange={(e) => {
                  handleChange(e);
                }}
                size={"small"}
              />
            </Stack>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Stack direction={"row"} justifyContent={"center"} gap={"2rem"} sx={{ width: "100%" }}>
            <Button onClick={handleClose} variant="text" color="info">
              Cancel
            </Button>
            <Button onClick={handleAdd} variant="contained" color="success">
              Add
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <Snackbar open={openSnackbar} autoHideDuration={5000} onClose={handleCloseSnackbar}>
        <Alert
          onClose={handleCloseSnackbar} //
          severity="success"
          variant="filled"
          sx={{ width: "100%", "& svg": { color: "white" }, "& div": { color: "white" } }}
          icon={false}
        >
          Network added!
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};
